import React, { useState, useEffect, useMemo } from 'react';
import api from '@/api/axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ChartBar, Phone, Users as UsersIcon, FileText, CurrencyDollar,
  Buildings, Tag, ArrowsClockwise, TrendUp, TrendDown, Star, Target,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { SeguimientoCotizaciones } from '@/components/SeguimientoCotizaciones';
import { AlertasGerenciales } from '@/components/AlertasGerenciales';
import { BandejaProductos } from '@/components/pizarra/BandejaProductos';

const Q = (n) => `Q${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const COLORS = ['#6C3FB5', '#7C3AED', '#10B981', '#F59E0B', '#EC4899', '#06B6D4', '#84CC16', '#EF4444', '#F472B6', '#0EA5E9'];

const PERIODS = [
  { id: 'today', label: 'Hoy' },
  { id: 'yesterday', label: 'Ayer' },
  { id: 'week', label: 'Esta semana' },
  { id: 'last_week', label: 'Semana pasada' },
  { id: 'month', label: 'Este mes' },
  { id: 'last_month', label: 'Mes pasado' },
  { id: 'mtd', label: 'Mes a la fecha' },
  { id: 'custom', label: 'Rango personalizado…' },
];

// Simple horizontal bar chart (pure CSS, no external deps)
const HBar = ({ items, valueKey, labelKey, total, formatter, color = '#6C3FB5' }) => {
  if (!items || items.length === 0) return <p className="text-xs text-[var(--text-muted)] italic py-4 text-center">Sin datos</p>;
  const max = Math.max(...items.map(i => Number(i[valueKey]) || 0)) || 1;
  return (
    <div className="space-y-1.5">
      {items.map((item, i) => {
        const v = Number(item[valueKey]) || 0;
        const pct = (v / max) * 100;
        return (
          <div key={i}>
            <div className="flex justify-between text-[11px] mb-0.5">
              <span className="font-bold text-[var(--text)] truncate max-w-[70%]" title={item[labelKey]}>{item[labelKey]}</span>
              <span className="text-[var(--text-muted)]">{formatter ? formatter(v) : v}</span>
            </div>
            <div className="h-2 bg-[var(--surface-muted)] rounded overflow-hidden">
              <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Simple donut chart from CSS conic gradient
const Donut = ({ data }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <p className="text-xs text-[var(--text-muted)] italic py-4 text-center">Sin datos</p>;
  let acc = 0;
  const stops = data.map((d, i) => {
    const start = (acc / total) * 360;
    acc += d.value;
    const end = (acc / total) * 360;
    return `${COLORS[i % COLORS.length]} ${start}deg ${end}deg`;
  }).join(', ');
  return (
    <div className="flex items-center gap-4">
      <div
        className="w-28 h-28 rounded-full shrink-0"
        style={{ background: `conic-gradient(${stops})` }}
      >
        <div className="w-full h-full rounded-full flex items-center justify-center" style={{ background: 'radial-gradient(circle, var(--surface) 0%, var(--surface) 55%, transparent 56%)' }}>
          <div className="text-center">
            <div className="text-lg font-black text-[var(--text)]">{total}</div>
            <div className="text-[9px] uppercase text-[var(--text-muted)]">total</div>
          </div>
        </div>
      </div>
      <div className="space-y-1 flex-1">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-3 h-3 rounded" style={{ background: COLORS[i % COLORS.length] }} />
            <span className="flex-1 text-[var(--text)]">{d.label}</span>
            <span className="font-bold text-[var(--text-muted)]">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, color = '#6C3FB5' }) => (
  <Card className="p-3 bg-[var(--surface)] border-[var(--border)]" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-2 mb-1">
      <div className="p-1.5 rounded-md" style={{ background: `${color}20` }}>
        <Icon size={16} weight="bold" style={{ color }} />
      </div>
      <span className="text-[10px] uppercase font-bold tracking-wider text-[var(--text-muted)]">{label}</span>
    </div>
    <div className="text-2xl font-black text-[var(--text)] leading-tight">{value}</div>
    {sub && <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{sub}</div>}
  </Card>
);

const ERP_PERIODS = [
  { k: 'day', l: 'Hoy' },
  { k: 'week', l: 'Semana' },
  { k: 'month', l: 'Mes' },
  { k: 'year', l: 'Año' },
];

function HarpErpVentasCard() {
  const [period, setPeriod] = useState('day');
  const [empresaErp, setEmpresaErp] = useState('complementos');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noAccess, setNoAccess] = useState(false);
  const fmt = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/harperp/resumen-ventas?period=${period}&empresa=${empresaErp}`, { timeout: 120000 })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive && e?.response?.status === 403) setNoAccess(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period, empresaErp]);

  if (noAccess) return null;
  const max = data?.ranking?.[0]?.total || 1;

  return (
    <Card className="p-4 bg-[var(--surface)] border-[var(--border)]" data-testid="harperp-ventas-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          <h3 className="text-sm font-black text-[var(--text)]">🏭 Ventas HarpERP <span className="text-[10px] font-bold text-[var(--text-faint)] uppercase">facturación oficial · {empresaErp === 'soluciones' ? 'SA' : 'CyH'}</span></h3>
          {data && <p className="text-[10px] text-[var(--text-muted)]">{data.rango.desde}{data.rango.desde !== data.rango.hasta ? ` a ${data.rango.hasta}` : ''}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex gap-1 bg-[var(--bg)] border border-[var(--border)] rounded-md p-0.5">
            {[{ k: 'complementos', l: 'CyH' }, { k: 'soluciones', l: 'SA' }].map((e) => (
              <button key={e.k} onClick={() => setEmpresaErp(e.k)}
                className={`px-3 py-1 text-[11px] font-bold rounded ${empresaErp === e.k ? 'bg-emerald-600 text-white' : 'text-[var(--text-muted)]'}`}
                data-testid={`erp-empresa-${e.k}`}>
                {e.l}
              </button>
            ))}
          </div>
          <div className="flex gap-1 bg-[var(--bg)] border border-[var(--border)] rounded-md p-0.5">
            {ERP_PERIODS.map((p) => (
              <button key={p.k} onClick={() => setPeriod(p.k)}
                className={`px-3 py-1 text-[11px] font-bold rounded ${period === p.k ? 'bg-[#6C3FB5] text-white' : 'text-[var(--text-muted)]'}`}
                data-testid={`erp-period-${p.k}`}>
                {p.l}
              </button>
            ))}
          </div>
        </div>
      </div>
      {loading ? (
        <p className="text-center py-6 text-xs text-[var(--text-muted)]">Consultando HarpERP{period === 'year' ? ' (año completo, puede tardar)' : ''}…</p>
      ) : data && (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-[10px] p-3 text-center">
              <p className="text-[9px] uppercase font-bold text-emerald-600">Facturado</p>
              <p className="text-lg font-black text-[var(--text)]" data-testid="erp-total">{fmt(data.total)}</p>
            </div>
            <div className="bg-blue-500/5 border border-blue-500/30 rounded-[10px] p-3 text-center">
              <p className="text-[9px] uppercase font-bold text-blue-600">Documentos</p>
              <p className="text-lg font-black text-[var(--text)]">{data.documentos}</p>
              {data.por_tipo && (
                <p className="text-[9px] text-[var(--text-muted)]">📄 {data.por_tipo.factura.docs} fact. · 🚚 {data.por_tipo.envio.docs} env.</p>
              )}
            </div>
            <div className="bg-amber-500/5 border border-amber-500/30 rounded-[10px] p-3 text-center">
              <p className="text-[9px] uppercase font-bold text-amber-600">Promedio</p>
              <p className="text-lg font-black text-[var(--text)]">{fmt(data.promedio)}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase font-bold text-[var(--text-faint)] mb-1.5">Top vendedores</p>
              {data.ranking.length === 0 ? <p className="text-xs text-[var(--text-muted)]">Sin ventas en el período</p> : (
                <div className="space-y-1.5">
                  {data.ranking.slice(0, 5).map((v, i) => (
                    <div key={v.vendedor} className="flex items-center gap-2 text-xs">
                      <span className="w-6 text-center font-black">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}</span>
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <b>{v.vendedor}</b>
                          <b className="text-emerald-600">{fmt(v.total)}</b>
                        </div>
                        <div className="h-1 bg-[var(--bg)] rounded-full overflow-hidden mt-0.5">
                          <div className="h-full bg-emerald-500" style={{ width: `${Math.round(v.total / max * 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-[var(--text-faint)] mb-1.5">Por sucursal</p>
              <div className="space-y-1">
                {data.por_sucursal.map((s) => (
                  <div key={s.sucursal} className="flex justify-between text-xs border-b border-[var(--border)] py-1 last:border-b-0">
                    <span>{s.sucursal} <span className="text-[var(--text-faint)]">({s.ventas})</span></span>
                    <b className="text-emerald-600">{fmt(s.total)}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

export default function AdminDashboard() {
  const [period, setPeriod] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [vendorId, setVendorId] = useState('all');
  const [branch, setBranch] = useState('all');
  const [activityType, setActivityType] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') {
        if (!customStart || !customEnd) {
          setLoading(false);
          return;
        }
        params.start = customStart;
        params.end = customEnd;
      }
      if (vendorId !== 'all') params.vendor_id = vendorId;
      if (branch !== 'all') params.branch = branch;
      if (activityType !== 'all') params.activity_type = activityType;
      const { data: d } = await api.get('/work-plan/admin-overview', { params });
      setData(d);
    } catch (e) {
      toast.error('Error al cargar overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get('/admin/users').then(r => setUsers(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, customStart, customEnd, vendorId, branch, activityType]);

  const download = async (fmt) => {
    try {
      const params = { period };
      if (period === 'custom') { params.start = customStart; params.end = customEnd; }
      if (vendorId !== 'all') params.vendor_id = vendorId;
      if (branch !== 'all') params.branch = branch;
      if (activityType !== 'all') params.activity_type = activityType;
      const res = await api.get(`/work-plan/admin-overview/export/${fmt}`, { params, responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: fmt === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Radiografia_Grupo_Sach_${period}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(`Error al exportar ${fmt.toUpperCase()}`);
    }
  };

  const sendWeeklyEmail = async () => {
    if (!window.confirm('¿Enviar el reporte ejecutivo ahora por correo a complementossa@gmail.com?')) return;
    try {
      toast.loading('Enviando reporte por correo…', { id: 'weekly-mail' });
      const { data: r } = await api.post('/admin/reports/weekly-email/test');
      if (r.status === 'success') {
        toast.success(`Reporte enviado a ${r.to.join(', ')}`, { id: 'weekly-mail' });
      } else {
        toast.error(`No se pudo enviar: ${r.reason || r.error || 'error'}`, { id: 'weekly-mail' });
      }
    } catch (e) {
      toast.error('Error al enviar el reporte por correo', { id: 'weekly-mail' });
    }
  };

  const donutData = useMemo(() => {
    if (!data) return [];
    const k = data.kpis;
    return [
      { label: 'Llamadas', value: k.calls },
      { label: 'Visitas', value: k.visits },
      { label: 'Cotizaciones', value: k.quotes },
      { label: 'Ventas', value: k.sales },
    ];
  }, [data]);

  if (loading && !data) return <div className="p-8 text-center text-[var(--text-muted)]">Cargando dashboard…</div>;
  const kpis = data?.kpis || {};
  const branches = data?.by_branch || [];
  const branchOptions = Array.from(new Set(branches.map(b => b.branch).filter(b => b && b !== '(Sin sucursal)')));

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="bg-gradient-to-r from-[#4B1D73] to-[#6C3FB5] rounded-2xl px-4 py-3 w-full sm:w-auto">
          <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2">
            <ChartBar size={24} weight="duotone" className="text-[#EDE5F7]" />
            Mi Día — Centro de Control Grupo SACH
          </h1>
          <p className="text-xs text-[#EDE5F7]">Radiografía comercial y gerencial · CRM + HarpERP</p>
        </div>
        <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={load} className="border-[var(--border)]" data-testid="dashboard-refresh">
          <ArrowsClockwise size={14} className="mr-1" /> Actualizar
        </Button>
        <Button size="sm" onClick={() => download('pdf')} className="bg-red-600 hover:bg-red-700 text-white" data-testid="dashboard-pdf">
          PDF
        </Button>
        <Button size="sm" onClick={() => download('excel')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dashboard-excel">
          Excel
        </Button>
        <Button size="sm" onClick={sendWeeklyEmail} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="dashboard-send-email">
          📧 Enviar por correo
        </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="p-3 bg-[var(--surface)] border-[var(--border)]">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="bg-[var(--bg)] border-[var(--border)]" data-testid="filter-period"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={vendorId} onValueChange={setVendorId}>
            <SelectTrigger className="bg-[var(--bg)] border-[var(--border)]" data-testid="filter-vendor"><SelectValue placeholder="Vendedor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los vendedores</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger className="bg-[var(--bg)] border-[var(--border)]" data-testid="filter-branch"><SelectValue placeholder="Sucursal" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las sucursales</SelectItem>
              {branchOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={activityType} onValueChange={setActivityType}>
            <SelectTrigger className="bg-[var(--bg)] border-[var(--border)]" data-testid="filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las actividades</SelectItem>
              <SelectItem value="call">Llamadas</SelectItem>
              <SelectItem value="visit">Visitas</SelectItem>
              <SelectItem value="quote">Cotizaciones</SelectItem>
              <SelectItem value="sale">Ventas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {period === 'custom' && (
          <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-[var(--border)]">
            <label className="text-xs">
              <span className="block text-[10px] font-bold text-[var(--text-muted)] uppercase mb-1">Desde</span>
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                     className="w-full bg-[var(--bg)] border border-[var(--border)] rounded px-2 py-1.5 text-sm text-[var(--text)]"
                     data-testid="custom-start" />
            </label>
            <label className="text-xs">
              <span className="block text-[10px] font-bold text-[var(--text-muted)] uppercase mb-1">Hasta</span>
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                     className="w-full bg-[var(--bg)] border border-[var(--border)] rounded px-2 py-1.5 text-sm text-[var(--text)]"
                     data-testid="custom-end" />
            </label>
          </div>
        )}
        <p className="text-[10px] text-[var(--text-muted)] mt-2">
          Rango consultado: <b className="text-[var(--text)]">{data?.period?.start?.slice(0,10) || '—'} → {data?.period?.end?.slice(0,10) || '—'}</b>
        </p>
      </Card>

      {/* Ventas HarpERP (facturación oficial) — lo primero que ve el admin */}
      <HarpErpVentasCard />

      {/* Alertas gerenciales: automáticas + manuales */}
      <AlertasGerenciales users={users} />

      {/* Bandeja de productos propuestos desde la Pizarra (solo si hay pendientes) */}
      <BandejaProductos />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Kpi icon={Phone} label="Llamadas" value={kpis.calls || 0} color="#3B82F6" />
        <Kpi icon={UsersIcon} label="Visitas" value={kpis.visits || 0} color="#8B5CF6" />
        <Kpi icon={FileText} label="Cotizaciones" value={kpis.quotes || 0} sub={Q(kpis.amount_quoted)} color="#F59E0B" />
        <Kpi icon={CurrencyDollar} label="Ventas" value={kpis.sales || 0} sub={Q(kpis.amount_sold)} color="#10B981" />
        <Kpi icon={ChartBar} label="Total actividades" value={kpis.total_activities || 0} color="#06B6D4" />
        <Kpi icon={CurrencyDollar} label="Monto vendido" value={Q(kpis.amount_sold)} sub={`Cotizado: ${Q(kpis.amount_quoted)}`} color="#EF4444" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 bg-[var(--surface)] border-[var(--border)]">
          <h3 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-3">Distribución de actividades</h3>
          <Donut data={donutData} />
        </Card>

        <Card className="p-4 bg-[var(--surface)] border-[var(--border)]">
          <h3 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-3 flex items-center gap-2">
            <Buildings size={14} /> Por sucursal (monto)
          </h3>
          <HBar items={branches} valueKey="amount_sold" labelKey="branch" formatter={Q} color="#10B981" />
        </Card>

        <Card className="p-4 bg-[var(--surface)] border-[var(--border)]">
          <h3 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-3 flex items-center gap-2">
            <Tag size={14} /> Productos más cotizados
          </h3>
          <HBar items={data?.by_category || []} valueKey="count" labelKey="category" formatter={(v) => `${v} cot.`} color="#F59E0B" />
        </Card>
      </div>

      {/* Cumplimiento del equipo — Meta vs Real */}
      <Card className="p-4 bg-[var(--surface)] border-[var(--border)]" data-testid="team-goals-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-black uppercase tracking-wider text-[var(--text)] flex items-center gap-2">
            <Target size={14} weight="bold" /> Cumplimiento del equipo vs metas
          </h3>
          <p className="text-[10px] text-[var(--text-muted)]">
            Metas mensuales prorrateadas al período seleccionado · <span className="text-emerald-500">≥100%</span> · <span className="text-blue-500">≥70%</span> · <span className="text-amber-500">≥40%</span> · <span className="text-red-500">&lt;40%</span>
          </p>
        </div>
        {(!data?.team_goals || data.team_goals.length === 0) ? (
          <p className="text-xs text-[var(--text-muted)] italic py-4 text-center">Sin vendedores con metas ni actividad en este período. Configura metas desde Admin → Usuarios → Metas.</p>
        ) : (
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full text-xs min-w-[720px]">
              <thead>
                <tr className="text-[10px] uppercase text-[var(--text-muted)] border-b border-[var(--border)]">
                  <th className="text-left py-2 font-black">Vendedor</th>
                  <th className="text-center py-2 font-black">Llamadas</th>
                  <th className="text-center py-2 font-black">Visitas</th>
                  <th className="text-center py-2 font-black">Cotizaciones</th>
                  <th className="text-center py-2 font-black">Ventas #</th>
                  <th className="text-right py-2 font-black">Monto (real/meta)</th>
                </tr>
              </thead>
              <tbody>
                {data.team_goals.map((tg) => {
                  const pctColor = (p) => p === null || p === undefined ? 'text-[var(--text-faint)]'
                    : p >= 100 ? 'text-emerald-500'
                    : p >= 70 ? 'text-blue-500'
                    : p >= 40 ? 'text-amber-500' : 'text-red-500';
                  const cell = (real, target, pct) => (
                    <div className="text-center">
                      <div className="text-sm font-black text-[var(--text)]">{real}</div>
                      <div className="text-[10px] text-[var(--text-muted)]">
                        meta: {target === null || target === undefined ? '—' : Math.round(target)}
                      </div>
                      {pct !== null && pct !== undefined && (
                        <div className={`text-[10px] font-black ${pctColor(pct)}`}>{pct}%</div>
                      )}
                    </div>
                  );
                  return (
                    <tr key={tg.user_id} className="border-b border-[var(--border)] last:border-0" data-testid={`team-goal-row-${tg.user_id}`}>
                      <td className="py-2">
                        <div className="text-sm font-bold text-[var(--text)]">{tg.user_name}</div>
                        <div className="text-[10px] text-[var(--text-muted)]">
                          {tg.has_goal ? <span className="text-[var(--text-muted)]">meta configurada</span> : <span className="text-amber-500">sin meta</span>} · {tg.role || '—'}
                        </div>
                      </td>
                      <td className="py-2">{cell(tg.actual.calls, tg.targets.calls, tg.pct.calls)}</td>
                      <td className="py-2">{cell(tg.actual.visits, tg.targets.visits, tg.pct.visits)}</td>
                      <td className="py-2">{cell(tg.actual.quotes, tg.targets.quotes, tg.pct.quotes)}</td>
                      <td className="py-2 text-center">
                        <div className="text-sm font-black text-emerald-500">{tg.actual.sales}</div>
                      </td>
                      <td className="py-2 text-right">
                        <div className="text-sm font-black text-[var(--text)]">{Q(tg.actual.amount_sold)}</div>
                        <div className="text-[10px] text-[var(--text-muted)]">meta: {tg.targets.amount === null || tg.targets.amount === undefined ? '—' : Q(tg.targets.amount)}</div>
                        {tg.pct.amount !== null && tg.pct.amount !== undefined && (
                          <div className={`text-[10px] font-black ${pctColor(tg.pct.amount)}`}>{tg.pct.amount}%</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Comparativo vs periodo anterior + Top 5 clientes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4 bg-[var(--surface)] border-[var(--border)]" data-testid="comparative-card">
          <h3 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-3 flex items-center gap-2">
            <TrendUp size={14} weight="bold" /> Comparativo vs periodo anterior
          </h3>
          {!data?.comparison ? (
            <p className="text-xs text-[var(--text-muted)] italic py-4 text-center">Sin datos</p>
          ) : (
            <div className="space-y-2">
              {[
                { key: 'amount_sold', label: 'Monto vendido', isMoney: true },
                { key: 'amount_quoted', label: 'Monto cotizado', isMoney: true },
                { key: 'sales', label: 'Ventas' },
                { key: 'quotes', label: 'Cotizaciones' },
                { key: 'visits', label: 'Visitas' },
                { key: 'calls', label: 'Llamadas' },
                { key: 'total_activities', label: 'Total actividades' },
              ].map(({ key, label, isMoney }) => {
                const curr = data.kpis[key] || 0;
                const prev = data.comparison.previous[key] || 0;
                const pct = data.comparison.delta_pct[key];
                const up = pct >= 0;
                const fmt = (v) => isMoney ? Q(v) : Number(v).toLocaleString('es-GT');
                return (
                  <div key={key} className="flex items-center justify-between gap-2 py-1.5 border-b border-[var(--border)] last:border-0">
                    <span className="text-xs text-[var(--text-muted)] flex-shrink-0">{label}</span>
                    <div className="flex items-center gap-2 text-right">
                      <span className="text-xs text-[var(--text-muted)] hidden sm:inline">{fmt(prev)} →</span>
                      <span className="text-sm font-bold text-[var(--text)]">{fmt(curr)}</span>
                      <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${up ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                        {up ? <TrendUp size={10} weight="bold" /> : <TrendDown size={10} weight="bold" />}
                        {Math.abs(pct).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                );
              })}
              <p className="text-[10px] text-[var(--text-muted)] mt-2">
                Periodo anterior: {data.comparison.previous_period.start.slice(0,10)} → {data.comparison.previous_period.end.slice(0,10)}
              </p>
            </div>
          )}
        </Card>

        <Card className="p-4 bg-[var(--surface)] border-[var(--border)]" data-testid="top-clients-card">
          <h3 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-3 flex items-center gap-2">
            <Star size={14} weight="fill" className="text-amber-500" /> Top 5 clientes — {PERIODS.find(p => p.id === period)?.label}
          </h3>
          {(!data?.top_clients || data.top_clients.length === 0) ? (
            <p className="text-xs text-[var(--text-muted)] italic py-4 text-center">Sin clientes con actividad</p>
          ) : (
            <div className="space-y-2">
              {data.top_clients.map((c, i) => {
                const totalAmt = (c.amount_sold || 0) + (c.amount_quoted || 0);
                return (
                  <div key={i} className="flex items-start justify-between gap-2 py-2 border-b border-[var(--border)] last:border-0">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <span className="text-xs font-black text-[#6C3FB5] flex-shrink-0 mt-0.5">#{i + 1}</span>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-[var(--text)] truncate" title={c.client_name}>{c.client_name}</div>
                        <div className="text-[10px] text-[var(--text-muted)] flex flex-wrap gap-x-2">
                          {c.quotes > 0 && <span>{c.quotes} cot.</span>}
                          {c.sales > 0 && <span className="text-emerald-600">{c.sales} venta{c.sales > 1 ? 's' : ''}</span>}
                          <span>{c.activities} actividad{c.activities > 1 ? 'es' : ''}</span>
                          {c.phone && <span className="hidden sm:inline">· {c.phone}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-black text-[var(--text)]">{Q(totalAmt)}</div>
                      {c.amount_sold > 0 && c.amount_quoted > 0 && (
                        <div className="text-[10px] text-[var(--text-muted)]">V:{Q(c.amount_sold)} · C:{Q(c.amount_quoted)}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Top 10 vendedores CRM (vendido + cotizado) — reubicado abajo; el ranking oficial es el de HarpERP */}
      <Card className="p-4 bg-[var(--surface)] border-[var(--border)]">
        <h3 className="text-sm font-black uppercase tracking-wider text-[var(--text)] mb-1">Volumen comercial gestionado · Top 10</h3>
        <p className="text-[10px] text-[var(--text-muted)] mb-3">Suma de lo <b>vendido CRM + cotizado</b>. ⚠ NO representa facturación real ni oportunidades únicas — es el volumen que cada vendedor gestionó. La facturación oficial es la de HarpERP.</p>
        <HBar
          items={(data?.by_vendor || []).map(v => ({ ...v, total_amount: v.amount_sold + v.amount_quoted }))}
          valueKey="total_amount" labelKey="vendor_name" formatter={Q} color="#6C3FB5"
        />
      </Card>

      {/* Cotizaciones por dar seguimiento — reubicado al final */}
      <SeguimientoCotizaciones />
    </div>
  );
}
